// "Prepare for offline" — a global control that pre-loads the farm field
// modules' LOOKUP data (blocks, workers, chemicals, recipes, equipment, farm
// settings, …) into the persisted React Query cache, so a scout/sprayer can
// drive out of signal and still have full dropdowns to capture with.
//
// The data gap is the part the PWA can't solve on its own: the service worker
// precaches the app SHELL (JS/CSS) on install, but the per-tenant DATA only
// lands in the cache when you actually visit each page online. This button
// fetches all of it in one tap (see src/lib/offlineLookups.ts), confirms it,
// and remembers when it was last done. Outbound writes made offline are handled
// separately by the offline queue + its badge.
//
// Placement: rendered as a button in the Dashboard header (next to the pallet
// search trigger), so it sits with the other app controls instead of floating
// over the phone thumb zone. Never appears pre-auth.

import { useEffect, useState } from 'react'
import { CloudDownload, Check, X as CloseIcon, AlertTriangle, Loader2, WifiOff } from 'lucide-react'
import clsx from 'clsx'
import { useQueryClient } from '@tanstack/react-query'
import type { QueryKey } from '@tanstack/react-query'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { usePersistedState } from '@/hooks/usePersistedState'
import { withTimeout } from '@/lib/saveTimeout'
import { buildFarmOfflineLookups, FARM_OFFLINE_SCREENS, unitsHeld, type PrewarmTask } from '@/lib/offlineLookups'
import { useModuleGate } from '@/hooks/useCanOpenModule'
import { recentPersistRefusals, refusalsOwnedBy } from '@/lib/queryPersist'
import { reportError } from '@/lib/errorReporter'

// The persisted cache's maxAge is 24h (see main.tsx). Past that the prewarmed
// rows are dropped on restore, so a prep older than this is "expired".
const PREP_MAX_AGE_MS = 24 * 60 * 60 * 1000

// Re-prep tuning. A partial failure leaves the button grey, so operators re-tap
// — and before this, every re-tap re-fetched ALL ~30 units from scratch and
// re-triggered the timeout storm (UX-006). SKIP_FRESH_MS makes a re-tap skip
// any unit whose data is still recent in cache (it's good for offline for 24h),
// so a re-tap only retries what actually failed/expired.
const SKIP_FRESH_MS    = 10 * 60 * 1000   // treat cache fresher than this as already-prepped
const DATA_TIMEOUT_MS  = 25_000           // per lookup attempt (then we ABORT it)
const SCREEN_TIMEOUT_MS = 30_000          // per code-chunk import (can't be aborted)
const MAX_ATTEMPTS     = 3                 // attempts per data unit (retry-with-backoff)
const RETRY_BACKOFF_MS = [700, 1800]      // wait before attempt 2, 3
// Bounded concurrency. With abort-on-timeout (below) this is now a REAL in-flight
// cap — last sprint's POOL=4 wasn't, because timed-out requests kept running and
// hogging a marginal link, so effective concurrency still climbed wave on wave.
const POOL             = 3

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms))

export default function OfflinePrepButton() {
  const qc = useQueryClient()
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null
  // FM11 UF-07: prepare only what this person can open.
  const holds = useModuleGate()

  const [open,    setOpen]    = useState(false)
  const [running, setRunning] = useState(false)
  const [done,    setDone]    = useState(0)
  const [total,   setTotal]   = useState(0)
  const [failed,  setFailed]  = useState<string[]>([])
  /** Lookups that LOADED but were refused by the persist budget (F114). */
  const [unsaved, setUnsaved] = useState<string[]>([])
  const [preppedAt, setPreppedAt] = usePersistedState<string | null>('kf49.offlinePreppedAt', null)
  const [online,  setOnline]  = useState(typeof navigator !== 'undefined' ? navigator.onLine : true)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const on  = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online',  on)
    window.addEventListener('offline', off)
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off) }
  }, [])

  const prepAgeMs = preppedAt ? Date.now() - new Date(preppedAt).getTime() : null
  const ready     = prepAgeMs != null && prepAgeMs < PREP_MAX_AGE_MS

  // True when a lookup's data is already in cache and fresh enough that
  // re-fetching it would be wasted work (still good for offline). Lets a re-tap
  // after a partial failure retry ONLY the units that actually failed.
  function isFresh(key: QueryKey): boolean {
    const s = qc.getQueryState(key)
    return !!s && s.status === 'success' && Date.now() - s.dataUpdatedAt < SKIP_FRESH_MS
  }

  // Fetch one lookup with abort-on-timeout + retry-with-backoff. The abort is
  // the crux: withTimeout()'s Promise.race never cancels the request, so a
  // timed-out fetch used to keep hogging the connection — we now pass an
  // AbortController so a stuck unit actually frees the pipe before the next one
  // starts. A few short retries let a transient slow response converge.
  async function fetchUnit(t: PrewarmTask): Promise<unknown> {
    let lastErr: unknown
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) await sleep(RETRY_BACKOFF_MS[attempt - 1] ?? 1800)
      const ac = new AbortController()
      try {
        return await withTimeout(
          qc.fetchQuery({
            queryKey: t.queryKey,
            queryFn: () => t.queryFn(ac.signal),
            staleTime: 0,
            // Prep is FOR the whole offline shift, but without this the entry
            // took fetchQuery's default 2h gcTime: any page not opened online
            // within 2h of prepping had its prewarmed lookups evicted — from
            // memory AND (via the next dehydrate) the IndexedDB snapshot the
            // prep exists to fill. v5 keeps the MAX gcTime a query has seen,
            // so 24h here (the persist maxAge) wins even where the page's own
            // useQuery still runs the 2h default.
            gcTime: 24 * 60 * 60 * 1000,
            retry: false, // we own the retry loop
          }),
          `Prepare ${t.label}`,
          DATA_TIMEOUT_MS,
          () => ac.abort(),
        )
      } catch (e) {
        lastErr = e
        // A request we timed out on can still land its data in cache a moment
        // later (we aborted the wrapper, not necessarily before the bytes
        // arrived). If it's now fresh, count it as done instead of retrying.
        if (isFresh(t.queryKey)) return
      }
    }
    throw lastErr
  }

  async function prepare() {
    if (running || !online) return
    // FM03 UF-02: a unit that answers many keys (one per block) writes each
    // with the prep's 24h life, as fetchUnit does for its own key.
    const seed = (queryKey: QueryKey, data: unknown) =>
      qc.prefetchQuery({ queryKey, queryFn: () => data, staleTime: 0, gcTime: 24 * 60 * 60 * 1000 })
    const tasks = unitsHeld(buildFarmOfflineLookups({ tenantId, tenantKind: activeTenant?.kind ?? null, seed }), holds)
    setRunning(true)
    setFailed([])
    setUnsaved([])
    const errs: string[] = []
    // Why each unit failed, for the report below. A screen that failed every
    // re-tap since 01 Sept on an MFP spray phone left no trace anywhere.
    const causes: Record<string, string> = {}

    // DATA units: skip anything already fresh in cache, so a re-tap converges
    // instead of restarting the whole storm. Each is abort-bounded + retried.
    const dataUnits = tasks
      .filter(t => !isFresh(t.queryKey))
      .map(t => ({ label: t.label, run: () => fetchUnit(t) }))

    // SCREEN units: load each field module's code chunk so navigating to it
    // offline works even when the SW precache is incomplete on this device
    // (the "This section hasn't been saved for offline use yet" gap). The
    // import is idempotent — already-loaded modules resolve instantly — and
    // can't be aborted, so it's just timeout-guarded.
    const screenUnits = unitsHeld(FARM_OFFLINE_SCREENS, holds).map(s => ({
      label: s.label,
      run: () => withTimeout(s.load(), `Prepare ${s.label}`, SCREEN_TIMEOUT_MS),
    }))

    const units = [...dataUnits, ...screenUnits]
    setTotal(units.length)
    setDone(0)

    // Bounded worker pool — now a real in-flight cap thanks to abort-on-timeout.
    let cursor = 0
    async function worker(): Promise<void> {
      for (let i = cursor++; i < units.length; i = cursor++) {
        const u = units[i]
        try { await u.run() } catch (e) {
          errs.push(u.label)
          causes[u.label] = e instanceof Error ? e.message : String(e)
        }
        finally { setDone(d => d + 1) }
      }
    }
    await Promise.all(Array.from({ length: Math.min(POOL, units.length) || 1 }, worker))
    setFailed(errs)
    if (errs.length > 0) {
      void reportError(new Error(`offline prep: ${errs.length} unit(s) didn't load`), {
        source: 'manual',
        level: 'warning',
        context: { causes, tenantId },
      })
    }
    // F114: a lookup can FETCH fine and still not be saved — isPersistSafe
    // fails closed on a payload past its node budget, and said nothing, so
    // the prep reported success and the orchard met an empty picker. Ask
    // what the dehydrate refused while we were prepping.
    const refused = recentPersistRefusals()
    // Warn only about the prep's own lookups: a register the user happened to
    // open (too large, not needed by a capture form offline) is not theirs to fix.
    setUnsaved(refusalsOwnedBy(refused, tasks.map(t => String(t.queryKey[0]))))
    if (refused.length > 0) {
      void reportError(new Error(`offline prep: ${refused.length} lookup(s) too large to save offline`), {
        source: 'manual',
        level: 'warning',
        context: { keys: refused, tenantId },
      })
    }
    // Only stamp "ready" when everything cached — a partial prep leaves the
    // prior (or absent) stamp so the UI doesn't over-promise offline coverage.
    // (Re-prep is now incremental, so re-tapping to clear a partial is cheap.)
    if (errs.length === 0) setPreppedAt(new Date().toISOString())
    setRunning(false)
  }

  const pct = total > 0 ? Math.round((done / total) * 100) : 0

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={ready ? `Ready for offline · prepared ${formatRelative(preppedAt!)}` : 'Prepare this device for offline use'}
        className="btn-ghost btn-sm relative print:hidden"
      >
        <CloudDownload size={16} className={ready ? 'text-emerald-400' : undefined} />
        <span className="hidden sm:inline">{ready ? 'Offline ready' : 'Work offline'}</span>
        {/* status dot — green when a fresh prep exists */}
        <span className={clsx(
          'absolute top-0.5 right-0.5 w-2 h-2 rounded-full',
          ready ? 'bg-emerald-400' : 'bg-gray-500',
        )} />
      </button>

      {open && (
        <div data-theme="dark" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4"
          onClick={() => !running && setOpen(false)}>
          <div className="surface-sunk border hairline rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90dvh] flex flex-col"
            onClick={e => e.stopPropagation()}>
            <header className="px-4 py-3 border-b hairline flex items-center justify-between">
              <h2 className="font-bold text-white flex items-center gap-2">
                <CloudDownload size={16} className="text-emerald-400" />
                Work offline
              </h2>
              <button onClick={() => !running && setOpen(false)} disabled={running}
                aria-label="Close — stop preparing offline data"
                className="tap-target p-1.5 -m-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-surface disabled:opacity-40">
                <CloseIcon size={16} />
              </button>
            </header>

            <div className="px-4 py-3 space-y-3 overflow-y-auto">
              {activeTenant?.kind === 'PACKHOUSE' ? (
                <p className="text-sm text-fg-secondary">
                  Prepare <span className="text-white font-medium">Piece Work Capture</span> for{' '}
                  <span className="text-white font-medium">no signal</span>. It loads the
                  workers, rates and settings the station needs.
                </p>
              ) : (
                <p className="text-sm text-fg-secondary">
                  Pre-load the field screens plus the blocks, workers, chemicals,
                  equipment and settings their forms need, so you can capture scouts,
                  picks, sprays, fuel and more with{' '}
                  <span className="text-white font-medium">no signal</span> — even on
                  screens you haven’t opened yet.
                </p>
              )}

              {/* Status line */}
              <div className={clsx(
                'rounded-lg px-3 py-2 text-sm flex items-center gap-2 border',
                ready ? 'bg-emerald-950/40 border-emerald-900/50 text-emerald-200'
                      : 'bg-surface/60 hairline-strong text-fg-secondary',
              )}>
                {ready ? <Check size={15} className="shrink-0" /> : <CloudDownload size={15} className="shrink-0" />}
                <span>
                  {ready
                    ? <>Ready for offline — prepared {formatRelative(preppedAt!)}.</>
                    : preppedAt
                      ? <>Last prepared {formatRelative(preppedAt)} — refresh it before heading out.</>
                      : <>Not prepared yet on this device.</>}
                </span>
              </div>

              {/* Progress while running */}
              {running && (
                <div className="space-y-1.5">
                  <div className="h-2 rounded-full surface overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[11px] text-gray-400 tabular-nums">Loading… {done}/{total}</p>
                </div>
              )}

              {/* Partial-failure note */}
              {!running && failed.length > 0 && (
                <div className="rounded-lg px-3 py-2 bg-amber-950/40 border border-amber-900/50 text-amber-200 text-[12px] flex items-start gap-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">{failed.length} item{failed.length === 1 ? '' : 's'} didn’t load — try again on a better connection:</p>
                    <p className="text-amber-300/80 mt-0.5">{failed.join(', ')}</p>
                  </div>
                </div>
              )}

              {/* F114: fetched, but not saved for offline. A different fact
                  from "didn't load", and the one that used to be silent. */}
              {!running && unsaved.length > 0 && (
                <div className="rounded-lg px-3 py-2 bg-amber-950/40 border border-amber-900/50 text-amber-200 text-[12px] flex items-start gap-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">
                      {unsaved.length} lookup{unsaved.length === 1 ? '' : 's'} loaded but {unsaved.length === 1 ? 'was' : 'were'} too large to save for offline.
                    </p>
                    <p className="text-amber-300/80 mt-0.5">
                      {unsaved.join(', ')} — {unsaved.length === 1 ? 'it' : 'they'} will reload when you have signal, so the screens that use {unsaved.length === 1 ? 'it' : 'them'} may be empty on a cold start in the field. Tell the office.
                    </p>
                  </div>
                </div>
              )}

              {!online && (
                <div className="rounded-lg px-3 py-2 bg-amber-950/40 border border-amber-900/50 text-amber-200 text-[12px] flex items-center gap-2">
                  <WifiOff size={14} className="shrink-0" />
                  You’re offline — reconnect to prepare (it needs to download the data first).
                </div>
              )}

              <p className="text-[11px] text-gray-500">
                Tip: app updates are handled by the “Update” banner; this only refreshes
                the data your forms read. Your offline-saved work syncs from the queue badge.
              </p>
            </div>

            <footer className="px-4 py-3 border-t hairline flex justify-end">
              <button
                onClick={prepare}
                disabled={running || !online}
                className={clsx(
                  'rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-colors',
                  running || !online
                    ? 'surface text-gray-500 cursor-not-allowed'
                    : 'bg-emerald-700 hover:bg-emerald-600 text-white',
                )}>
                {running ? <Loader2 size={15} className="animate-spin" /> : <CloudDownload size={15} />}
                {running ? 'Preparing…' : ready ? 'Refresh offline data' : 'Prepare now'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  )
}

// "just now" / "5 min ago" / "2 h ago" / date — local to this component.
function formatRelative(iso: string): string {
  const diffSec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (diffSec < 45)     return 'just now'
  if (diffSec < 3600)   return `${Math.floor(diffSec / 60)} min ago`
  if (diffSec < 86_400) return `${Math.floor(diffSec / 3600)} h ago`
  return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })
}
